// Enhancement UI Component
// MU Online style equipment enhancement interface with visual feedback

import type { EnhancementResult, EnhancedItem, EnhancementStone } from '@systems/EnhancementSystem';

export interface EnhancementUICallbacks {
  onEnhance: (itemInstanceId: string, stoneId?: string) => EnhancementResult;
  getEnhancedItem: (instanceId: string) => EnhancedItem | undefined;
  getSuccessRate: (currentLevel: number, stoneId?: string) => number;
  getEnhancementStones: () => EnhancementStone[];
}

export class EnhancementUI {
  private container: HTMLDivElement;
  private isOpen: boolean = false;
  private callbacks: EnhancementUICallbacks | null = null;
  private selectedItemId: string | null = null;
  private selectedStoneId: string | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'enhancement-ui';
    this.container.innerHTML = `
      <div class="enhancement-panel">
        <div class="enhancement-header">
          <h2>⚒️ Equipment Enhancement</h2>
          <button class="close-btn">×</button>
        </div>
        <div class="enhancement-content">
          <div class="item-slot">
            <div class="slot-label">Item to Enhance</div>
            <div class="item-display" id="enhance-item-slot">
              <span class="placeholder">Drop item here</span>
            </div>
            <div class="item-info" id="enhance-item-info"></div>
          </div>

          <div class="arrow">→</div>

          <div class="stone-selection">
            <div class="slot-label">Enhancement Stone</div>
            <div class="stones-list" id="stones-list"></div>
          </div>

          <div class="enhancement-info">
            <div class="success-rate">
              Success Rate: <span id="success-rate">0%</span>
            </div>
            <div class="warning" id="destruction-warning"></div>
          </div>

          <button class="enhance-btn" id="enhance-btn" disabled>
            ✨ Enhance
          </button>

          <div class="result-display" id="result-display"></div>
        </div>
      </div>
    `;

    this.applyStyles();
    this.setupEventListeners();
    document.body.appendChild(this.container);
    this.hide();
  }

  private applyStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      #enhancement-ui {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 2000;
      }

      #enhancement-ui .enhancement-panel {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 2px solid #4a4a6a;
        border-radius: 10px;
        padding: 20px;
        min-width: 400px;
        box-shadow: 0 0 30px rgba(0, 0, 0, 0.8);
      }

      #enhancement-ui .enhancement-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #4a4a6a;
        padding-bottom: 10px;
        margin-bottom: 20px;
      }

      #enhancement-ui .enhancement-header h2 {
        margin: 0;
        color: #ffcc00;
        font-size: 20px;
      }

      #enhancement-ui .close-btn {
        background: none;
        border: none;
        color: #ff4444;
        font-size: 24px;
        cursor: pointer;
        padding: 0 10px;
      }

      #enhancement-ui .close-btn:hover {
        color: #ff6666;
      }

      #enhancement-ui .enhancement-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 15px;
      }

      #enhancement-ui .item-slot,
      #enhancement-ui .stone-selection {
        text-align: center;
      }

      #enhancement-ui .slot-label {
        color: #888;
        font-size: 12px;
        margin-bottom: 5px;
      }

      #enhancement-ui .item-display {
        width: 80px;
        height: 80px;
        background: rgba(0, 0, 0, 0.4);
        border: 2px dashed #666;
        border-radius: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        transition: border-color 0.2s;
      }

      #enhancement-ui .item-display:hover {
        border-color: #888;
      }

      #enhancement-ui .item-display.has-item {
        border: 2px solid #4a4a6a;
        background: rgba(0, 0, 0, 0.6);
      }

      #enhancement-ui .item-display .placeholder {
        color: #666;
        font-size: 12px;
        text-align: center;
      }

      #enhancement-ui .item-display .item-icon {
        font-size: 40px;
      }

      #enhancement-ui .item-display .enhancement-level {
        position: absolute;
        bottom: 2px;
        right: 5px;
        color: #ffcc00;
        font-size: 14px;
        font-weight: bold;
        text-shadow: 1px 1px 2px #000;
      }

      #enhancement-ui .item-info {
        color: #aaa;
        font-size: 12px;
        margin-top: 5px;
        min-height: 20px;
      }

      #enhancement-ui .arrow {
        color: #666;
        font-size: 24px;
      }

      #enhancement-ui .stones-list {
        display: flex;
        gap: 10px;
        justify-content: center;
      }

      #enhancement-ui .stone-item {
        width: 50px;
        height: 50px;
        background: rgba(0, 0, 0, 0.4);
        border: 2px solid #4a4a6a;
        border-radius: 6px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s;
      }

      #enhancement-ui .stone-item:hover {
        border-color: #888;
        transform: scale(1.05);
      }

      #enhancement-ui .stone-item.selected {
        border-color: #ffcc00;
        box-shadow: 0 0 10px rgba(255, 204, 0, 0.5);
      }

      #enhancement-ui .stone-item .stone-icon {
        font-size: 20px;
      }

      #enhancement-ui .stone-item .stone-tier {
        font-size: 8px;
        color: #888;
      }

      #enhancement-ui .stone-item.common { border-color: #888; }
      #enhancement-ui .stone-item.blessed { border-color: #4488ff; }
      #enhancement-ui .stone-item.divine { border-color: #ff88ff; }

      #enhancement-ui .enhancement-info {
        text-align: center;
        padding: 10px;
        background: rgba(0, 0, 0, 0.3);
        border-radius: 5px;
        width: 100%;
      }

      #enhancement-ui .success-rate {
        color: #fff;
        font-size: 18px;
      }

      #enhancement-ui .success-rate span {
        color: #00ff00;
        font-weight: bold;
      }

      #enhancement-ui .success-rate span.low {
        color: #ff4444;
      }

      #enhancement-ui .success-rate span.medium {
        color: #ffcc00;
      }

      #enhancement-ui .warning {
        color: #ff8844;
        font-size: 12px;
        margin-top: 5px;
      }

      #enhancement-ui .enhance-btn {
        background: linear-gradient(135deg, #4a4a6a 0%, #2a2a4a 100%);
        border: 2px solid #6a6a8a;
        color: #fff;
        padding: 12px 40px;
        font-size: 16px;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.2s;
      }

      #enhancement-ui .enhance-btn:hover:not(:disabled) {
        background: linear-gradient(135deg, #5a5a7a 0%, #3a3a5a 100%);
        transform: scale(1.02);
      }

      #enhancement-ui .enhance-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      #enhancement-ui .result-display {
        min-height: 40px;
        padding: 10px;
        text-align: center;
        font-size: 16px;
        font-weight: bold;
      }

      #enhancement-ui .result-display.success {
        color: #00ff00;
        animation: success-glow 0.5s ease-out;
      }

      #enhancement-ui .result-display.failure {
        color: #ff4444;
      }

      #enhancement-ui .result-display.destroyed {
        color: #ff0000;
        animation: shake 0.5s ease-out;
      }

      @keyframes success-glow {
        0% { text-shadow: 0 0 20px #00ff00; }
        100% { text-shadow: none; }
      }

      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-10px); }
        75% { transform: translateX(10px); }
      }

      /* Glow effects for enhanced items */
      #enhancement-ui .item-display.glow-blue {
        box-shadow: 0 0 15px #4444ff;
      }
      #enhancement-ui .item-display.glow-purple {
        box-shadow: 0 0 15px #ff44ff;
      }
      #enhancement-ui .item-display.glow-red {
        box-shadow: 0 0 15px #ff4444;
      }
      #enhancement-ui .item-display.glow-gold {
        box-shadow: 0 0 20px #ffcc00;
        animation: golden-glow 1s infinite;
      }

      @keyframes golden-glow {
        0%, 100% { box-shadow: 0 0 15px #ffcc00; }
        50% { box-shadow: 0 0 25px #ffdd44; }
      }
    `;
    document.head.appendChild(style);
  }

  private setupEventListeners(): void {
    const closeBtn = this.container.querySelector('.close-btn') as HTMLButtonElement;
    closeBtn.addEventListener('click', () => this.hide());

    const enhanceBtn = this.container.querySelector('#enhance-btn') as HTMLButtonElement;
    enhanceBtn.addEventListener('click', () => this.performEnhancement());

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.hide();
      }
    });

    // Close on clicking outside
    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.hide();
      }
    });
  }

  public setCallbacks(callbacks: EnhancementUICallbacks): void {
    this.callbacks = callbacks;
  }

  public show(itemInstanceId?: string): void {
    this.container.style.display = 'flex';
    this.isOpen = true;

    if (itemInstanceId) {
      this.selectItem(itemInstanceId);
    }

    this.updateStonesList();
    this.updateUI();
  }

  public hide(): void {
    this.container.style.display = 'none';
    this.isOpen = false;
    this.selectedItemId = null;
    this.selectedStoneId = null;
  }

  public isUIOpen(): boolean {
    return this.isOpen;
  }

  public selectItem(instanceId: string): void {
    this.selectedItemId = instanceId;
    this.updateUI();
  }

  private updateStonesList(): void {
    if (!this.callbacks) return;

    const stonesList = this.container.querySelector('#stones-list') as HTMLDivElement;
    const stones = this.callbacks.getEnhancementStones();

    stonesList.innerHTML = stones.map(stone => `
      <div class="stone-item ${stone.tier} ${this.selectedStoneId === stone.id ? 'selected' : ''}"
           data-stone-id="${stone.id}">
        <span class="stone-icon">💎</span>
        <span class="stone-tier">${stone.tier}</span>
      </div>
    `).join('');

    // Add click handlers
    stonesList.querySelectorAll('.stone-item').forEach((el) => {
      el.addEventListener('click', () => {
        const stoneId = (el as HTMLElement).dataset.stoneId!;
        this.selectedStoneId = this.selectedStoneId === stoneId ? null : stoneId;
        this.updateStonesList();
        this.updateUI();
      });
    });
  }

  private updateUI(): void {
    if (!this.callbacks) return;

    const itemSlot = this.container.querySelector('#enhance-item-slot') as HTMLDivElement;
    const itemInfo = this.container.querySelector('#enhance-item-info') as HTMLDivElement;
    const successRateEl = this.container.querySelector('#success-rate') as HTMLSpanElement;
    const warningEl = this.container.querySelector('#destruction-warning') as HTMLDivElement;
    const enhanceBtn = this.container.querySelector('#enhance-btn') as HTMLButtonElement;
    const resultDisplay = this.container.querySelector('#result-display') as HTMLDivElement;

    // Clear previous result
    resultDisplay.textContent = '';
    resultDisplay.className = 'result-display';

    if (!this.selectedItemId) {
      itemSlot.innerHTML = '<span class="placeholder">Drop item here</span>';
      itemSlot.className = 'item-display';
      itemInfo.textContent = '';
      successRateEl.textContent = '0%';
      warningEl.textContent = '';
      enhanceBtn.disabled = true;
      return;
    }

    const item = this.callbacks.getEnhancedItem(this.selectedItemId);
    const level = item?.enhancementLevel ?? 0;

    // Update item display
    itemSlot.innerHTML = `
      <span class="item-icon">🗡️</span>
      ${level > 0 ? `<span class="enhancement-level">+${level}</span>` : ''}
    `;
    itemSlot.className = 'item-display has-item ' + this.getGlowClass(level);

    itemInfo.textContent = level > 0 ? `Current: +${level}` : 'Not enhanced';

    // Update success rate
    const successRate = this.callbacks.getSuccessRate(level, this.selectedStoneId ?? undefined);
    const ratePercent = Math.round(successRate * 100);
    successRateEl.textContent = `${ratePercent}%`;
    successRateEl.className = ratePercent <= 25 ? 'low' : ratePercent <= 50 ? 'medium' : '';

    // Show destruction warning
    if (level >= 6) {
      warningEl.textContent = '⚠️ Item may be destroyed or downgraded on failure!';
    } else {
      warningEl.textContent = '';
    }

    // Enable button if item is selected and not at max
    enhanceBtn.disabled = level >= 15;
    if (level >= 15) {
      enhanceBtn.textContent = 'MAX LEVEL';
    } else {
      enhanceBtn.textContent = `✨ Enhance to +${level + 1}`;
    }
  }

  private getGlowClass(level: number): string {
    if (level >= 15) return 'glow-gold';
    if (level >= 12) return 'glow-red';
    if (level >= 9) return 'glow-purple';
    if (level >= 5) return 'glow-blue';
    return '';
  }

  private performEnhancement(): void {
    if (!this.callbacks || !this.selectedItemId) return;

    const result = this.callbacks.onEnhance(this.selectedItemId, this.selectedStoneId ?? undefined);
    const resultDisplay = this.container.querySelector('#result-display') as HTMLDivElement;

    resultDisplay.textContent = result.message;
    if (result.success) {
      resultDisplay.className = 'result-display success';
    } else if (result.destroyed) {
      resultDisplay.className = 'result-display destroyed';
      this.selectedItemId = null;
    } else {
      resultDisplay.className = 'result-display failure';
    }

    this.updateUI();
  }

  public destroy(): void {
    this.container.remove();
  }
}
